import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {
  AsyncHandler,
  AsyncHandlerOf,
  HandlerOf,
} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {CrmSyncSourcesEnum} from '../../../../models/api/crmSyncModel';
import {Skeleton} from '../../../common/skeleton';
import {TooltipExplicitCondition} from '../../../common/tooltips/tooltipExplicitCondition';
import {CrmSyncDialogStep} from '../crmSyncDialogStep';
import {computeCrmSyncSourceDisplayName} from './crmSyncHelpers';

/*
 * Props.
 */

interface CrmSyncStepOneProps {
  isAdmin: boolean;
  hasExpiredCredentials: boolean;
  isConnected: boolean;
  crmSyncSource: CrmSyncSourcesEnum;
  username?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  crmLogoImgSrc: string;
  signInToIntegrationAccount: HandlerOf<boolean>;
  signOutOfIntegrationAsync: AsyncHandler;
}

/*
 * Style.
 */

const StyledButton = styled(Button)`
  display: inline-block;
  margin-bottom: 5px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
`;

const StyledButtonTextWrapperDiv = styled.div`
  flex: 1;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  display: inline-block;
`;

interface StyledButtonImageWrapperDivProps {
  $isDisabled?: boolean;
}

const StyledButtonImageWrapperDiv = styled.div<StyledButtonImageWrapperDivProps>`
  background: ${(p) => p.theme.greys.white};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 20px;
  height: 20px;
  margin: -1px 8px -2px -9px;

  ${(p) =>
    p.$isDisabled &&
    css`
      filter: grayscale(1);
    `};
`;

const StyledDetailsWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  gap: 8px;
`;

interface StyledUsernameWrapperDivProps {
  $hasExpiredCredentials: boolean;
}

const StyledUsernameWrapperDiv = styled.div<StyledUsernameWrapperDivProps>`
  display: inline-flex;
  flex-flow: row;
  background: ${(p) => p.theme.palette.green.shade20};
  border-radius: 15px;
  padding: 7px 12px 7px 7px;

  ${(p) =>
    p.$hasExpiredCredentials &&
    css`
      background: ${p.theme.palette.red.shade20};
    `};
`;

const StyledIcon = styled(Icon)`
  flex: 0 0 16px;
  margin-right: 8px;
`;

const StyledUsernameDetailsDiv = styled.div`
  flex: 1;
  line-height: 16px;
`;

const StyledUsernameSpan = styled.span`
  font-weight: ${fontWeights.semibold};
`;

const StyledCredentialsErrorDiv = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.verySmall};
  flex: 0 0 100%;
`;

/*
 * Component.
 */

export const CrmSyncStepOne: FC<CrmSyncStepOneProps> = (props) => {
  const {
    isDisabled,
    crmSyncSource,
    username,
    isLoading,
    hasExpiredCredentials,
    isConnected,
    signOutOfIntegrationAsync,
    signInToIntegrationAccount,
  } = props;

  const onEditCredentialsAsync = async (shouldUseTestEnv: boolean) => {
    await signOutOfIntegrationAsync();
    signInToIntegrationAccount(shouldUseTestEnv);
  };
  const localize = useLocalize();

  const crmSyncSourceName = computeCrmSyncSourceDisplayName(crmSyncSource, localize);
  const shouldRenderConnectButton = !isConnected || (!username && hasExpiredCredentials);
  return (
    <CrmSyncDialogStep
      step={1}
      title={renderLogInTo(crmSyncSourceName)}
      description={renderStepOneDescription(crmSyncSourceName)}
      isLoading={isLoading}
      renderLoadingState={renderLoadingState}
      isDisabled={isDisabled}
    >
      {shouldRenderConnectButton
        ? renderConnectButton(props, crmSyncSourceName)
        : renderUsername(props, crmSyncSourceName, onEditCredentialsAsync)}
    </CrmSyncDialogStep>
  );
};

/*
 * Helpers.
 */

function renderLoadingState() {
  return <Skeleton width="170px" height="30px" borderRadius="15px" />;
}

function renderLogInTo(crmSyncSourceName: string) {
  return (
    <>
      <Trans id="accounts-crm-dialog-step-1-log-in">Log in to </Trans>
      {` ${crmSyncSourceName}`}
    </>
  );
}

function renderStepOneDescription(crmSyncSourceName: string) {
  return (
    <>
      {`${crmSyncSourceName} `}
      <Trans id="accounts-crm-dialog-step-1-description">administrator privileges required.</Trans>
    </>
  );
}

function renderConnectButtonTooltip(crmSyncSourceName: string) {
  return (
    <>
      <Trans id="accounts-crm-dialog-step-1-disabled-admin-tooltip">
        Contact your Front administrator to connect
      </Trans>
      {` ${crmSyncSourceName}.`}
    </>
  );
}

function renderNonAdminTooltip() {
  return <Trans id="accounts-crm-dialog-admin-tooltip">Only Front administrators can make edits</Trans>;
}

function maybeRenderAuthorizedText(
  hasExpiredCredentials: boolean,
  crmSyncSourceName: string,
  username?: string,
) {
  if (hasExpiredCredentials) {
    return null;
  }
  if (!username) {
    return (
      <>
        {`${crmSyncSourceName} `}
        <Trans id="accounts-crm-dialog-step-1-account-authorized">account authorized</Trans>
      </>
    );
  }
  return (
    <>
      {' '}
      <Trans id="accounts-crm-dialog-step-1-authorized">authorized</Trans>
    </>
  );
}

function renderUsername(
  props: CrmSyncStepOneProps,
  crmSyncSourceName: string,
  onEditCredentialsAsync: AsyncHandlerOf<boolean>,
) {
  const {hasExpiredCredentials, isAdmin, isDisabled, username} = props;
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledDetailsWrapperDiv>
          <StyledUsernameWrapperDiv $hasExpiredCredentials={hasExpiredCredentials}>
            <StyledIcon
              name={hasExpiredCredentials ? 'warningFilledSmall' : 'checkmarkCircleSmall'}
              color={hasExpiredCredentials ? theme.palette.red.shade40 : theme.palette.green.shade40}
            />
            <StyledUsernameDetailsDiv>
              {username && <StyledUsernameSpan>{username}</StyledUsernameSpan>}
              {maybeRenderAuthorizedText(hasExpiredCredentials, crmSyncSourceName, username)}
            </StyledUsernameDetailsDiv>
          </StyledUsernameWrapperDiv>
          <TooltipCoordinator render={renderNonAdminTooltip} placement="top">
            <TooltipExplicitCondition isEnabled={!isAdmin} />
            <Button
              variant={ButtonVariantsEnum.LINK}
              color={PaletteColorsEnum.RED}
              isDisabled={!isAdmin || isDisabled}
              onClick={(event) => onEditCredentialsAsync(event.shiftKey)}
            >
              <Trans id="accounts-crm-dialog-step-1-disconnect">Disconnect</Trans>
            </Button>
          </TooltipCoordinator>
          {maybeRenderCredentialsError(crmSyncSourceName, hasExpiredCredentials)}
        </StyledDetailsWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderCredentialsError(crmSyncSourceName: string, hasExpiredCredentials: boolean) {
  if (!hasExpiredCredentials) {
    return null;
  }
  return (
    <StyledCredentialsErrorDiv>
      An error occurred while connecting to {crmSyncSourceName}. Please re-connect credentials.
    </StyledCredentialsErrorDiv>
  );
}

function renderConnectButton(props: CrmSyncStepOneProps, crmSyncSourceName: string) {
  const {crmLogoImgSrc, isAdmin, isDisabled, hasExpiredCredentials, signInToIntegrationAccount} = props;

  return (
    <StyledTooltipCoordinator render={() => renderConnectButtonTooltip(crmSyncSourceName)}>
      <TooltipExplicitCondition isEnabled={!isAdmin} />
      <StyledButton
        variant={ButtonVariantsEnum.PRIMARY}
        isDisabled={!isAdmin || isDisabled}
        onClick={(event) => signInToIntegrationAccount(event.shiftKey)}
      >
        <StyledButtonContentDiv>
          <StyledButtonImageWrapperDiv $isDisabled={!isAdmin}>
            <img src={crmLogoImgSrc} alt="crm logo" />
          </StyledButtonImageWrapperDiv>
          <StyledButtonTextWrapperDiv>{renderLogInTo(crmSyncSourceName)}</StyledButtonTextWrapperDiv>
        </StyledButtonContentDiv>
      </StyledButton>
      {maybeRenderCredentialsError(crmSyncSourceName, hasExpiredCredentials)}
    </StyledTooltipCoordinator>
  );
}
